import React from 'react';
import { Feature } from '@tencent/workbench';
import BaseDrawerFeature from '@framework/workbench/common/feature/base-drawer-feature';
import { FillType, FillTypeMap, FillTypes } from '../../../../fill/constants';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { SolidFill as SolidFillColor } from '@src/slide/domain/ui-model/plugin/referModels/FillClass';
import { PropertyEditorShowAction } from '@src/js/constant';
import { checkIsSlideBg } from '@framework/workbench/drawer/utils';
import { FormItemName, INPUT_PROPS_CALLBACK_KEY } from '@framework/workbench/drawer/constants';
import { TdwDrawerConstMap, tdwDrawerReport } from '@framework/workbench/drawer/report';
import {
    IBackgroundFillInfo,
    IBaseFillInfo,
    IGradFill,
    ISolidFill,
} from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { ISolidFillProperty } from '@src/slide/domain/ui-model/element-modeles/property/fill/solid-fill';
import type { IFillFeature } from './types';
import { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IEmptyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/empty-model.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { AppEntryType } from '@src/slide/infrastructure/entry/base/infrastructure-base.interface';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { ColorSelectButton } from '@framework/workbench/drawer/components/color-select-button';
import { GradFill } from '@framework/workbench/drawer/ui/chart/common/fill/base/details/gradual-fill';
import { ISolidFillProps, SolidFill } from '@framework/workbench/drawer/ui/chart/common/fill/base/details/solid-fill';
import {
    TDWVer5Type,
    TDWVer6Type,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { STSchemeColorVal } from '@tencent/slide-sdk-pb-model';
import { AngleFrom, NumberInputFrom } from '@tencent/wb-ext-form';
import { handleInputBlurInitAndReset, IInputParams } from '@src/slide/application/service/drawer-input-manager/drawer-handler';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import EmptyModel from '@src/slide/domain/ui-model/element-modeles/empty-model';

const DEFAULT_BG_TD_COLOR = new TDColor({
    themeKey: STSchemeColorVal.bg1,
});

@Feature()
export class FillFeature extends BaseDrawerFeature implements IFillFeature {
    public gradFill: GradFill;
    public solidFill: SolidFill;
    public curFillType;

    private dropDom;

    public constructor() {
        super();
        this.initFillClass();
        this.addInitPropsCallback(this.getInputParams().key);
    }

    public initPropsCallback = () => {
        this.curFillType = this.fillType;
        if (this.fillType === FillType.SOLID_FILL) {
            this.solidFill?.initPropsCb(this.fillInfo as ISolidFill);
        } else if (this.fillType === FillType.GRADUAL_FILL) {
            this.gradFill?.initPropsCb(this.fillInfo as IGradFill, this.fillColor);
        }
    };

    public get fillType(): string {
        const type = (this.getSelectModel() as IMultiplyModel | IElementBaseModel | IEmptyModel)?.getBackgroundFill?.()
            ?.getFillType();
        return type ? FillTypeMap[type] : FillType.NO_SELECT;
    }

    public renderGradualFill() {
        this.curFillType = FillType.GRADUAL_FILL;
        return this.gradFill.renderGradualFill(this.fillColor.gradFill);
    }

    public getFillTypes = () => {
        const fillTypes: {key: string, content: string}[] = [];
        const propertyMenu = this.getPropertyMenu();
        let isSlideBg = false;
        propertyMenu?.items?.forEach((item) => {
            if (item.id === ActionType.FILLCOLOR) {
                isSlideBg = !!item?.isSlideBg;
            }
        });
        const { fillColor } = this;
        FillTypes.forEach((item) => {
            if (this.needFilterFillType(item, isSlideBg, fillColor?.pattFill)) {
                // 设置幻灯片背景没有无填充
            } else {
                fillTypes.push(item);
            }
        });
        return fillTypes;
    };

    public handleFillStyleChange = () => {
        this.gradFill.value.presetItem = null;  // 切换填充，需要清空presetItem
        let ver5 = 1;
        const updateStyleMap = {
            [FillType.NO_FILL]: () => this.solidFill.confirmSolidChoice(null),
            [FillType.SOLID_FILL]: () => {
                ver5 = 2;
                // 判断是否为slide背景
                if (this.isSlideBg) {
                    this.solidFill.confirmSolidChoice(DEFAULT_BG_TD_COLOR);
                } else {
                    this.solidFill.confirmSolidChoice(this.solidFill.value.solidFillColor);
                }
            },
            [FillType.GRADUAL_FILL]: () => {
                ver5 = 3;
                this.gradFill.confirmGradChoice();
            },
        };
        updateStyleMap[this.curFillType]?.();
        // 上报数据
        this.tdwDrawerReport(FormItemName.FILL_SELECT, { ver5 });
    };

    public getRotateWidthShape = (): boolean => {
        if (this.curFillType === FillType.GRADUAL_FILL) {
            return this.gradFill.value.rotateWithShape === 1;
        }
        return false;
    };

    public onCheckChange = (value: boolean) => {
        if (this.curFillType === FillType.GRADUAL_FILL) {
            this.gradFill.value.rotateWithShape = value ? 1 : 0;
            this.gradFill.confirmGradChoice();
            this.tdwDrawerReport(TdwDrawerConstMap.gradualRotateWithShape);
        }
    };

    public setBgWithAction = (action) => {
        if (action === PropertyEditorShowAction.FROM_GRADUAL_FILL) {
            this.gradFill.confirmGradChoice();
        }
    };

    public getSlideBgProps = () => {
        const map = {
            [FillType.SOLID_FILL]: { solidFill: new SolidFillColor(this.solidFill.value.solidFillColor) },
            [FillType.GRADUAL_FILL]: this.gradFill.getGradualProps(),
            [FillType.NO_FILL]: { noFill: 1 },
        };
        if (map[this.curFillType]) {
            return map[this.curFillType];
        }
    };

    public get isMasterEditType(): boolean {
        const appServices = AppServiceProvider.getInstance().getAppServices();
        if (browser.isMobile
            || appServices.getEntryType() !== AppEntryType.PC_EDIT) {
            return false;
        }
        return (appServices as IPcEditorAppServices).editableServiceLayer
            ?.masterLayoutEditService.isMasterEditType ?? false;
    }

    /**
     * 应用到全部
     */
    public handleAllSlideBgUse = (): void => {
        if (this.isSlideBg) {
            const bgProps = this.getSlideBgProps();
            this.getUiDataService().presentation.useSlideBgInMaster(bgProps);
            tdwDrawerReport(TdwDrawerConstMap.applyAllBg);
        }
    };

    /**
     * 重置背景
     */
    public handleSlideBgReset = (): void => {
        if (this.isSlideBg) {
            this.getSelectionService().currentSlide?.getBackgroundFill().updateCurrentFill({});
            tdwDrawerReport(TdwDrawerConstMap.resetBg);
        }
    };

    /**
     * 重置背景按钮是否置灰
     */
    public get isResetBtnDisable()  {
        return this.getSelectionService().currentSlide
            ? !this.getSelectionService().currentSlide?.hasBackground : false;
    }

    public updateUIWithFillBgData = () => {
        const updateFillOp = {
            grpFill: this.updateGrpFill,   // 优先处理继承自父元素的填充
            gradFill: () => {
                this.curFillType = FillType.GRADUAL_FILL;
                this.gradFill.updateGradualFill(this.fillColor.gradFill);
            },  // 优先处理渐变填充
            solidFill: this.updateSolidFill,   // 无渐变，处理纯色填充,
            pattFill: this.updatePattFill,
        };
        const { fillColor } = this;
        this.dispatchSlideBgState();
        if (fillColor?.noFill === 1) {
            this.updateNoColor();
        } else {
            const index = Object.keys(updateFillOp).find(key => fillColor?.[key]);
            typeof index !== 'undefined' && updateFillOp[index]?.();
        }
    };

    public getSelectModel() {
        const model = super.getSelectModel();
        // 选中备注时，侧边栏填充展示为背景设置项
        if (model?.type === UIModelType.NOTE) {
            return new EmptyModel({});
        }
        return model;
    }

    public updateFillProps(value, onlyChangeUI?) {
        (this.getSelectModel() as IMultiplyModel | IElementBaseModel | IEmptyModel).getBackgroundFill?.()
            .updateCurrentFill(value, onlyChangeUI);
        this.dispatchSlideBgState();
    }

    public getColorSelectButton() {
        const value = this.getFillValue();
        return <ColorSelectButton
            onColorChange={(tdColor: TDColor, from) => {
                this.processTdColor(tdColor);
                this.solidFill.handleSolidColorChange(
                    tdColor,
                    from,
                    false,
                );
                this.getColorPickerManager()?.colorPickerTdwReport({
                    color: value?.srgb_color || '',
                    colorPickerFrom: TDWVer6Type.COLOR_PICKER_DRAWER,
                    colorGroupFrom: from,
                    actionType: TDWVer5Type.COLOR_PICKER_FILL,
                });
            }}
            value={value}
            paletteButtonsStyle={{
                width: 116,
                marginLeft: 'auto',
            }}
            colorBoxStyle={{
                width: 88,
            }}
        />;
    }

    @handleInputBlurInitAndReset()
    public handleSolidOpacityChange(opacity: number, from?: NumberInputFrom, dragEnd?: boolean): void {
        this.solidFill.handleSolidOpacityChange(opacity, from, dragEnd);
    }

    @handleInputBlurInitAndReset()
    public handleGradualOpacityChange(opacity: number, from?: NumberInputFrom, dragEnd?: boolean): void {
        this.gradFill.handleGradualOpacityChange(opacity, dragEnd, from);
    }

    @handleInputBlurInitAndReset()
    public handleAngleChange(value: number, dragEnd?: boolean, from?: AngleFrom): void {
        this.gradFill.handleAngleChange(value, dragEnd, from);
    }

    public getInputParams(): IInputParams {
        return {
            key: INPUT_PROPS_CALLBACK_KEY.FILL,
        };
    }

    /**
     * 处理tdColor, 图表fill-feature需要处理color
     */
    protected processTdColor(color: TDColor) {
        return color;
    }

    private changeFillSelectValue = (value: string) => {
        this.curFillType = value;
        this.handleFillStyleChange();
    };

    /**
    * FillTypes选项过滤
    */
    private needFilterFillType = (item, isSlideBg, pattFill) => (item.key === FillType.NO_FILL && isSlideBg)
        || (item.key === FillType.PATT_FILL && !pattFill);

    private handleFillPresetChange = (value) => {
        let ver5;
        if (value) {
            this.curFillType = value.curFillType;
            if (this.curFillType === FillType.SOLID_FILL) {
                this.solidFill.value.solidFillColor = value.solidFillColor;
                this.solidFill.confirmSolidChoice(value.solidFillColor);
                ver5 = 2;
            }
            if (this.curFillType === FillType.GRADUAL_FILL) {
                this.gradFill.value = {
                    ...this.gradFill.value,
                    ...value,
                };
                this.gradFill.confirmGradChoice();
                ver5 = 3;
            }
            this.hideColorDropDown();
        } else {
            this.curFillType = FillType.NO_FILL;
            this.solidFill.confirmSolidChoice(null);
            ver5 = 1;
        }
        // 上报数据
        this.tdwDrawerReport(FormItemName.FILL_PRESET, { ver5 });
    };

    private hideColorDropDown = () => {
        if (this.dropDom) {
            this.dropDom.hide();
        }
    };

    private updateSolidFill = () => {
        const { fillColor } = this;
        const { color } = fillColor?.solidFill as ISolidFill;
        if (color) {
            this.curFillType = FillType.SOLID_FILL;
            this.solidFill.value.solidFillColor = fillColor.solidFill?.color ? color : new TDColor({
                rgbaStr: fillColor.color,
            });
        }
    };

    private updatePattFill = () => {
        this.curFillType = FillType.PATT_FILL;
    };

    private updateGrpFill = () => {
        this.curFillType = FillType.NO_SELECT;
    };

    private dispatchSlideBgState = () => {
        if (this.isSlideBg) {
            window.dispatchEvent(new Event('slideBgChange'));
        }
    };

    /**
     * 具体某一种填充对象
     */
    private get fillInfo(): IBaseFillInfo | null {
        const fill = ((this.getSelectModel() as IMultiplyModel | IElementBaseModel | IEmptyModel)
            ?.getBackgroundFill?.()?.getCurrentFill() as ISolidFillProperty);
        return fill?.fillInfo;
    }

    /**
     * 所有填充数据集合
     */
    protected get fillColor(): IBackgroundFillInfo {
        const fillColor = this.getSelectModel()?.getBackgroundFill?.()
            .getFillInfo();
        return fillColor ? fillColor : this.getDefaultValue();
    }

    public get isSlideBg(): boolean {
        return checkIsSlideBg(this.getPropertyMenu()?.items);
    }

    private getDefaultValue = (): IBackgroundFillInfo => {
        const isMultiOrGroup = this.isMultiOrGroup();
        if (isMultiOrGroup || this.isTable()) {
            this.curFillType = FillType.NO_SELECT;
            return {};
        }
        if (this.isSlideBg) {
            this.curFillType = FillType.SOLID_FILL;
            return { solidFill: new SolidFillColor(new TDColor({ themeKey: STSchemeColorVal.bg1 })) };
        }
        this.curFillType = FillType.NO_FILL;
        return { noFill: 1 };
    };

    private updateNoColor() {
        this.curFillType = FillType.NO_FILL;
    }

    private initFillClass = () => {
        this.gradFill = new GradFill();
        const solidFillProp: ISolidFillProps = {};
        if (this.isSlideBg) {
            solidFillProp.themeKey = STSchemeColorVal.bg1;
        } else {
            solidFillProp.themeKey = STSchemeColorVal.accent1;
        }

        this.solidFill = new SolidFill(solidFillProp);
        // chart中重写了这两个方法
        this.gradFill.getSelectModel = this.getSelectModel.bind(this);
        this.solidFill.getSelectModel = this.getSelectModel.bind(this);
        this.gradFill.updateFillProps = this.updateFillProps.bind(this);
        this.solidFill.updateFillProps = this.updateFillProps.bind(this);
    };
}
